import CloseIcon from "@mui/icons-material/Close";
import { useEffect } from "react";
import type { Stage3EmailState } from "@/context/CampaignBuilderContext";
import type { ConfigTab } from "@/types/types";
import BrandingPanel from "./BrandingPanel";
import MessagePanel from "./MessagePanel";

interface CustomizeDrawerProps {
  open: boolean;
  onClose: () => void;
  emailState: Stage3EmailState;
  onEmailChange: (v: Partial<Stage3EmailState>) => void;
  activeTab: ConfigTab;
  onTabChange: (tab: ConfigTab) => void;
}

export default function CustomizeDrawer({
  open,
  onClose,
  emailState,
  onEmailChange,
  activeTab,
  onTabChange,
}: CustomizeDrawerProps) {
  // ESC key closes
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape" && open) onClose();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  return (
    <div
      className="flex flex-col bg-white fixed top-0 right-0 bottom-0"
      style={{
        width: 380,
        minWidth: 380,
        maxWidth: 380,
        flex: "none",
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1)",
        borderLeft: "1px solid var(--color-card-border)",
        boxShadow: "-12px 0 32px rgba(26, 22, 18, 0.10)",
        pointerEvents: open ? "auto" : "none",
        zIndex: 200,
        willChange: "transform",
        marginTop: "76px",
      }}
      aria-hidden={!open}
    >
      {/* Only render content when open to avoid stale Slate state */}
      {open && (
        <div className="flex flex-col h-full">
          {/* Drawer header */}
          <div
            className="flex items-center justify-between shrink-0"
            style={{
              padding: "14px 20px 12px",
              borderBottom: "1px solid var(--color-card-border)",
            }}
          >
            <h3
              style={{
                fontFamily: "var(--font-lato)",
                fontSize: "14px",
                fontWeight: 700,
                color: "var(--color-dark-dust)",
                textTransform: "uppercase",
                letterSpacing: "1.5px",
                margin: 0,
                lineHeight: "1",
              }}
            >
              Customize your Email
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center text-graphite hover:bg-wax hover:text-rodeo-black"
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 0,
                transition: "all 0.15s",
              }}
              aria-label="Close customize panel"
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </button>
          </div>

          {/* Config tabs */}
          <div
            className="flex shrink-0"
            style={{ borderBottom: "1px solid var(--color-card-border)", backgroundColor: "white" }}
          >
            {(["message", "branding"] as ConfigTab[]).map((tab) => {
              const isActive = activeTab === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => onTabChange(tab)}
                  className="flex-1 font-lato text-[13px] font-semibold text-center border-b-2 transition-all user-select-none"
                  style={{
                    padding: "14px 16px",
                    color: isActive ? "var(--color-rodeo-black)" : "var(--color-graphite)",
                    borderBottomColor: isActive ? "var(--color-rodeo-black)" : "transparent",
                    cursor: "pointer",
                  }}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              );
            })}
          </div>

          {/* Panel body — scrollable */}
          <div className="flex-1 overflow-y-auto" style={{ boxSizing: "border-box" }}>
            <div style={{ boxSizing: "border-box" }}>
              {activeTab === "message" ? (
                <MessagePanel state={emailState} onChange={onEmailChange} />
              ) : (
                <BrandingPanel state={emailState} onChange={onEmailChange} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
